import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  map,
  tap,
  take,
  skip
} from 'rxjs/operators';

import { IAPIListRequestOptions } from '@sondermind/utilities/models-http';

import { ClientPortalReferralsHttpService } from '../services/client-portal-referrals.httpservice';
import { ClientPortalReferral } from '../models/client-portal-referral.model';

/**
 * Store for managing client portal referrals data
 */
@Injectable({
  providedIn: 'root'
})
export class ClientPortalReferralsStore {
  private referralsSubject = new BehaviorSubject<ClientPortalReferral[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable of all client portal referrals
   */
  referrals$ = this.referralsSubject.asObservable();

  /**
   * Observable indicating if data is currently being loaded
   */
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private clientPortalReferralsHttpService: ClientPortalReferralsHttpService
  ) {}

  /**
   * Loads client portal referrals data
   * @param forceRefresh Whether to force refresh the data even if already loaded
   * @param opts Optional request options for filtering, sorting, etc.
   * @returns Observable of ClientPortalReferral array
   */
  loadReferrals$(
    forceRefresh: boolean = false,
    opts: Partial<IAPIListRequestOptions> = {}
  ): Observable<ClientPortalReferral[]> {
    if (forceRefresh || this.referralsSubject.value.length === 0) {
      this.isLoadingSubject.next(true);

      return this.clientPortalReferralsHttpService.fetchAll(opts).pipe(
        tap((referrals) => {
          this.referralsSubject.next(referrals);
          this.isLoadingSubject.next(false);
        })
      );
    }

    return this.referrals$;
  }

  /**
   * Refreshes the referrals data and updates the store
   * @param opts Optional request options for filtering, sorting, etc.
   * @returns Observable of ClientPortalReferral array
   */
  refresh(opts: Partial<IAPIListRequestOptions> = {}): Observable<ClientPortalReferral[]> {
    this.isLoadingSubject.next(true);

    this.clientPortalReferralsHttpService.fetchAll(opts).pipe(
      take(1),
    ).subscribe((referrals) => {
      this.referralsSubject.next(referrals);
      this.isLoadingSubject.next(false);
    });

    return this.referrals$.pipe(skip(1), take(1));
  }

  /**
   * Gets a specific referral by ID
   * @param id The ID of the referral to find
   * @returns Observable of ClientPortalReferral or null if not found
   */
  getReferralById$(id: number): Observable<ClientPortalReferral | null> {
    return this.referrals$.pipe(
      map((referrals) => referrals.find((referral) => referral.id === id) || null)
    );
  }
}
